/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */

import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import Button from 'carbon-react/lib/components/button';

import { getCreateConsolidationUrl, getPredictSettingsOrganisationsPageUrl } from '@futrli/shared-services';
import { ModalView, UILoader, UIWizardFlowHeader, UIWizardFlowProgressBar } from '@futrli/ui-components';

import { CoaStructure } from '../consolidation-content/coa-structure/coa-structure';
import { DailyFxRates } from '../consolidation-content/daily-fx-rates/daily-fx-rates';
import { Eliminations } from '../consolidation-content/eliminations/eliminations';
import { EliminationsQuestion } from '../consolidation-content/eliminations-question/eliminations-question';
import { Settings } from '../consolidation-content/settings/settings';
import { TABS } from '../constants';
import { useConsolidationContext } from '../consolidation-context/consolidation-context';
import { useConsolidationWizardContext } from '../consolidation-wizard-context/consolidation-wizard-context';
import styles from './create-consolidation.scss';

export const CreateConsolidation = () => {
  const navigate = useNavigate();
  const { workspaceId, tabId } = useParams();
  const {
    flags: { isReady }
  } = useConsolidationContext();
  const {
    currentStepNum,
    handleBack,
    handleNext,
    isBackEnabled,
    isNextEnabled,
    isSaving,
    progress,
    stepText
  } = useConsolidationWizardContext();

  const isQuestionPage =
    window.location.pathname === getCreateConsolidationUrl(workspaceId!, TABS.ELIMINATIONS_QUESTION);

  // ************
  // * Handlers *
  // ************

  const handleExit = useCallback(() => {
    navigate(getPredictSettingsOrganisationsPageUrl(workspaceId!, 'consolidations'));
  }, [navigate, workspaceId]);

  return (
    <ModalView
      contentClassName={styles.container}
      title={<FormattedMessage id="consolidation.new_consolidation.modal_title" defaultMessage="New consolidation" />}
      headerButtons={
        <Button buttonType="secondary" size="medium" onClick={handleExit}>
          <FormattedMessage id="common.cancel_and_exit" defaultMessage="Cancel and exit" />
        </Button>
      }
    >
      {!isReady ? (
        <UILoader isFull />
      ) : (
        <>
          <UIWizardFlowProgressBar value={progress} displaySubNav />
          {isQuestionPage ? (
            <EliminationsQuestion />
          ) : (
            <>
              <UIWizardFlowHeader
                disableBack={!isBackEnabled}
                disableNext={!isNextEnabled}
                innerContainerClassname={styles.headerInner}
                outerContainerClassname={styles.header}
                onBack={currentStepNum !== 0 ? handleBack : undefined}
                onNext={handleNext}
                title={stepText?.title || ''}
                description={stepText?.desc || ''}
                saving={isSaving}
              />
              <div className={styles.content}>
                <Routes>
                  <Route
                    path="*"
                    element={
                      <>
                        {tabId === TABS.SETTINGS && <Settings />}
                        {tabId === TABS.ELIMINATIONS && <Eliminations />}
                        {tabId === TABS.COA_STRUCTURE && <CoaStructure />}
                        {tabId === TABS.DAILY_FX_RATES && <DailyFxRates />}
                        {(!tabId || !Object.values(TABS).includes(tabId as TABS)) && (
                          <Navigate to={getCreateConsolidationUrl(workspaceId!, TABS.SETTINGS)} replace />
                        )}
                      </>
                    }
                  />
                </Routes>
              </div>
            </>
          )}
        </>
      )}
    </ModalView>
  );
};
